import type {
  DeploymentType,
  PoolDeploymentTypeV2,
  PoolDeploymentTypeV2Label
} from '@/api/organizations'
import { getDeploymentType, getPoolDeploymentTypeLabel } from '@/api/organizations'
import { useCloudApi } from '../api/cloudApi'
import type {
  CloudV1alpha1PoolRef,
  V1alpha1PoolMemberEnvironment,
  V1alpha1PoolOption
} from '@streamnative/cloud-api-client-typescript'
import { i18n } from '@/lang'
import commonConfig from 'Config/common_config.json'
import { asyncComputed } from '@vueuse/core'
import {
  hasRequiredEnvironmentFeatures,
  isServerlessLocationAvailable,
  isServerlessVkcEnabled
} from '@/utils/serverlessLocation'
import type { EnvironmentFeatureRequirements } from '@/utils/serverlessLocation'

const api = useCloudApi()
const { organization } = usePulsarState()
const { t } = i18n.global
const serverlessLocation = commonConfig.serverlessLocation
const serverlessLocationList = serverlessLocation.split(',')
const refresher = ref<number>(1)
const isRunning = ref<boolean>(false)

export interface PoolObject extends V1alpha1PoolOption {
  label: string
  deploymentType: DeploymentType
  deploymentTypeV2: PoolDeploymentTypeV2
  deploymentTypeLabel: PoolDeploymentTypeV2Label
}

const poolMap = asyncComputed<Record<string, PoolObject>>(
  async () => {
    if (!organization.value) {
      return {}
    }
    refresher.value
    let data
    try {
      ;({ data } = await api.listNamespacedPoolOption(organization.value))
    } catch (error: any) {
      const status = error?.status ?? error?.response?.status
      if (status === 403) {
        // User lacks permission to list pool options; treat as no available pools.
        return {}
      }
      throw error
    }
    const map: Record<string, PoolObject> = {}

    for (const item of data.items) {
      if (!item.spec) {
        throw Error(t('dashboard.errorGettingLocations'))
      }
      const deploymentType = getDeploymentType(item)
      const deploymentTypeV2 = deploymentType
        .replace('hosted', 'dedicated')
        .replace('managed', 'byoc') as PoolDeploymentTypeV2
      const poolKey = getPoolkey(item.spec.poolRef)

      map[poolKey] = {
        ...item,
        label: `${item.spec?.cloudType}/${deploymentTypeV2} - ${poolKey}`,
        deploymentType,
        deploymentTypeV2,
        deploymentTypeLabel: getPoolDeploymentTypeLabel(deploymentTypeV2)
      }
    }
    return map
  },
  {},
  { evaluating: isRunning }
)

const refreshPoolMap = () => {
  if (isRunning.value) {
    // it's running, ignore refresh request
    return
  }
  refresher.value += 1
}

const getPoolkey = (poolRef: CloudV1alpha1PoolRef) => {
  return `${poolRef.namespace}/${poolRef.name}`
}

const findPool = (poolRef?: CloudV1alpha1PoolRef) => {
  if (!poolRef) {
    return undefined
  }
  return poolMap.value[getPoolkey(poolRef)]
}

const isSamePoolRef = (left?: CloudV1alpha1PoolRef, right?: CloudV1alpha1PoolRef) => {
  return !!left && !!right && left.name === right.name && left.namespace === right.namespace
}

const getAllAvailableLocations = (
  type: DeploymentType | PoolDeploymentTypeV2 | 'all',
  infrastructure: string,
  isUrsa: boolean,
  options?: EnvironmentFeatureRequirements
) => {
  return (
    Object.values(poolMap.value)
      .filter((p: PoolObject) => infrastructure === 'all' || p.spec?.cloudType === infrastructure)
      .filter((p: PoolObject) => (isUrsa ? p.spec?.features?.URSA : true))
      .map((p: PoolObject) => {
        return (p.status?.environments ?? []).map(e => {
          return { environment: e, pool: p, label: `${p.label} / ${e.region}` }
        })
      })
      .flat() as { environment: V1alpha1PoolMemberEnvironment; pool: PoolObject; label: string }[]
  ).filter(
    ({ environment, pool }: { environment: V1alpha1PoolMemberEnvironment; pool: PoolObject }) => {
      const id = `${pool.spec?.cloudType}:${pool.spec?.poolRef.namespace}:${pool.spec?.poolRef.name}:${environment.region}`
      if (type === 'serverless') {
        return isServerlessLocationAvailable(id, environment, serverlessLocationList, options)
      }
      if (!hasRequiredEnvironmentFeatures(environment, options)) {
        return false
      }
      switch (type) {
        case 'all':
          return true
        case 'byoc':
        case 'dedicated':
          return pool.deploymentTypeV2 === type || pool.deploymentTypeV2 === `${type}-pro`
        case 'hosted':
        case 'managed':
          return pool.deploymentType === type || pool.deploymentType === `${type}-pro`
        case 'byoc-pro':
        case 'dedicated-pro':
          return pool.deploymentTypeV2 === type
        case 'hosted-pro':
        case 'managed-pro':
          return pool.deploymentType === type
      }
      return false
    }
  )
}

const isServerlessKafkaSupportedForPoolRef = (
  poolRef?: CloudV1alpha1PoolRef,
  infrastructure = 'all'
) => {
  if (!poolRef) {
    return false
  }

  return getAllAvailableLocations('serverless', infrastructure, false, {
    requireNativeKafka: true,
    requireSharedVkc: true
  }).some(location => isSamePoolRef(location.pool.spec?.poolRef, poolRef))
}

export const usePoolsV2 = () => {
  return {
    poolMap,
    refreshPoolMap,
    getAllAvailableLocations,
    findPool,
    isServerlessVkcEnabled,
    isSamePoolRef,
    isServerlessKafkaSupportedForPoolRef
  }
}
